import { Cloud, Gauge, HardDrive, Scissors, Sparkles, Trophy, Video } from "lucide-react";
import type { JSX, ReactNode } from "react";

import { GithubMark } from "@/components/icons";
import { buttonVariants } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { GITHUB_URL, RELEASES_URL, SUPPORTED_GAMES } from "@/lib/site";
import { cn } from "@/lib/utils";

const FEATURES: { icon: ReactNode; title: string; body: string }[] = [
  {
    icon: <Video className="size-5" />,
    title: "Records itself",
    body: "Watches for a game session and starts before the loading screen clears. Nothing to remember, nothing to press."
  },
  {
    icon: <Gauge className="size-5" />,
    title: "No frames lost",
    body: "A tuned OBS engine with hardware encoding, picked per machine. The whole point is that you cannot tell it is running."
  },
  {
    icon: <Trophy className="size-5" />,
    title: "Match data attached",
    body: "Every VOD carries its scoreboard, timeline, and events, so a game is searchable long after you have forgotten it."
  },
  {
    icon: <Scissors className="size-5" />,
    title: "Clip in seconds",
    body: "Trim on the timeline, pick your audio tracks, export. Built for the moment right after it happens."
  },
  {
    icon: <Cloud className="size-5" />,
    title: "Share in one click",
    body: "A clip becomes a link you can paste anywhere, with the scoreboard from the game it came from attached."
  },
  {
    icon: <HardDrive className="size-5" />,
    title: "Local by default",
    body: "Recordings live in your Videos folder as ordinary files. No account, no upload, no library you can be locked out of."
  }
];

export const LandingPage = (): JSX.Element => (
  <>
    <section className="mx-auto w-full max-w-5xl px-4 pt-16 pb-14 text-center sm:px-6 sm:pt-24 sm:pb-20">
      <Badge variant="default" className="mx-auto mb-5 gap-1.5 px-3 py-1">
        <Sparkles className="size-3" />
        Free and open source
      </Badge>

      <h1 className="text-4xl leading-[1.05] font-extrabold tracking-tight text-balance sm:text-6xl">
        Every match recorded.
        <br />
        <span className="bg-gradient-to-br from-primary to-primary/50 bg-clip-text text-transparent">
          Nothing to press.
        </span>
      </h1>

      <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-pretty text-muted-foreground sm:text-lg">
        OpenVOD watches for your game, records it with a tuned OBS engine, and files the VOD with
        its full match data. Clip a moment and share it in one click.
      </p>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <a
          href={RELEASES_URL}
          target="_blank"
          rel="noreferrer noopener"
          className={cn(buttonVariants({ size: "lg" }))}
        >
          Download for Windows
        </a>
        <a
          href={GITHUB_URL}
          target="_blank"
          rel="noreferrer noopener"
          className={cn(buttonVariants({ variant: "outline", size: "lg" }))}
        >
          <GithubMark className="size-4" />
          View source
        </a>
      </div>

      <p className="mt-5 text-sm text-muted-foreground">
        Full match data for {SUPPORTED_GAMES.join(", ")} · any other game records as a plain VOD
      </p>
    </section>

    <section className="mx-auto w-full max-w-6xl px-4 pb-16 sm:px-6 sm:pb-24">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((feature) => (
          <article key={feature.title} className="ov-surface p-5">
            <span className="grid size-10 place-items-center rounded-xl bg-primary/15 text-primary">
              {feature.icon}
            </span>
            <h2 className="mt-3.5 text-base font-bold tracking-tight">{feature.title}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{feature.body}</p>
          </article>
        ))}
      </div>
    </section>

    <section className="mx-auto w-full max-w-3xl px-4 pb-20 text-center sm:px-6 sm:pb-28">
      <div className="ov-surface px-6 py-10 sm:px-10">
        <h2 className="text-2xl font-bold tracking-tight text-balance sm:text-3xl">
          Someone sent you a clip?
        </h2>
        <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-muted-foreground">
          Clip links open right here, with the full scoreboard from the game they came from.
        </p>
      </div>
    </section>
  </>
);
